import React from 'react';
import { Text } from '../../../../design-system';
import { LAST_UPDATED_COLUMN } from '../../../shared-constants';
import {
  prettyPhone,
  formatCurrencyOnBlur,
  required,
  emailVal,
  phoneNumberVal,
} from '../../../../helpers/utils';
import DeadlineCell from '../../../../components/DeadlineCell';
import MemberNote from '../../../../components/MemberNote';
import EllipsisDropdown from '../../../../components/EllipsisDropdown';

export const BOARD_MEMBER = {
  TITLE: 'Board Information',
  FORM_FIELDS: [
    { label: 'Number of Board Members', name: 'board_count' },
    { label: 'Meeting Frequency', name: 'frequency' },
    { label: 'Quorum', name: 'quorum' },
    { label: 'Committees', name: 'committees' },
    { label: 'Term Length', name: 'term_length' },
    { label: 'Number of Terms Allowed', name: 'terms_allowed' },
    { label: 'Policies In Place', name: 'policy_count' },
    { label: '% Board Gave Last Year', name: 'board_giving_perc' },
    {
      label: '$ Board Gave Last Year',
      name: 'board_giving_currency',
      type: 'money',
      transformOnBlur: formatCurrencyOnBlur,
    },
    { label: 'Pro Bono/In-Kind Gifts', name: 'gifts' },
    { label: 'Board Notes', name: 'board_notes', multiline: true },
  ],
};

export const STAFF_MEMBER = {
  TITLE: 'Staff Information',
  FORM_FIELDS: [
    { label: 'Number of Full Time Staff', name: 'full_time_staff' },
    { label: 'Number of Part-Time Staff', name: 'part_time_staff' },
    { label: 'Number of Volunteers', name: 'volunteers' },
    { label: 'Turnover Last Year', name: 'turnover' },
    { label: 'Staff Notes', name: 'staff_notes', multiline: true },
    { label: 'Expiration Date', name: 'expires', type: 'date' },
  ],
};

const PRIMARY_COLUMNS = [
  {
    Header: 'Name',
    accessor: 'name',
    type: 'text',
  },
  {
    Header: 'Role',
    accessor: 'role',
    type: 'text',
    Cell: ({ cell: { value } }) => value && value,
  },
  {
    Header: 'Phone',
    accessor: 'phone',
    type: 'phone',
    Cell: ({ cell: { value } }) => value && prettyPhone(value),
  },
  {
    Header: 'Email',
    accessor: 'email',
    type: 'text',
    Cell: ({ cell: { value } }) => value && value,
  },
  { ...LAST_UPDATED_COLUMN },
];

const SECONDARY_COLUMNS = [
  {
    Header: 'Gender',
    accessor: 'gender',
    type: 'text',
    Cell: ({ cell: { value } }) => value && value,
  },
  {
    Header: 'Race',
    accessor: 'race',
    type: 'text',
    Cell: ({ cell: { value } }) => value && value,
  },
  {
    Header: 'Employer',
    accessor: 'employer',
    type: 'text',
    Cell: ({ cell: { value } }) => value && value,
  },
  {
    Header: 'Position',
    accessor: 'position',
    type: 'text',
    Cell: ({ cell: { value } }) => value && value,
  },
  {
    Header: 'Expires',
    accessor: 'expires',
    type: 'date',
    Cell: DeadlineCell,
  },
];

const ACTIONS_COLUMNS = [
  {
    Header: '',
    accessor: 'edit',
    Cell: ({ row: { original } }) => {
      const options = [
        {
          id: 1,
          onClick: () =>
            original.type === 'Board'
              ? original.openBoardEditModal(original)
              : original.openStaffEditModal(original),
          render: () => (
            <Text id="edit-member-button">Edit {original.type} Member</Text>
          ),
        },
        {
          id: 2,
          onClick: () => original.handleDeleteMember(original.id),
          render: () => <Text id="delete-member-button">Delete Member</Text>,
        },
      ];
      return <EllipsisDropdown options={options} />;
    },
  },
];

export const PRIMARY_SECONDARY_BOARD_MEMBER_COLUMNS = {
  primary: [
    ...PRIMARY_COLUMNS.map((column) => {
      if (column.accessor === 'role') {
        return { ...column, Header: 'Board Seat' };
      }
      return column;
    }),
  ],
  secondary: [
    ...SECONDARY_COLUMNS,
    {
      Header: 'Notes',
      accessor: 'notes',
      type: 'text',
      Cell: (props) => <MemberNote type="Board Member" {...props} />,
    },
  ],
  actions: [...ACTIONS_COLUMNS],
};

export const PRIMARY_SECONDARY_STAFF_MEMBER_COLUMNS = {
  primary: [...PRIMARY_COLUMNS],
  secondary: [
    ...SECONDARY_COLUMNS.filter(
      (column) =>
        column.accessor !== 'employer' && column.accessor !== 'position',
    ),
    {
      Header: 'Notes',
      accessor: 'notes',
      type: 'text',
      Cell: (props) => <MemberNote type="Staff Member" {...props} />,
    },
  ],
  actions: [...ACTIONS_COLUMNS],
};

export const MEMBER_MODAL_FIELDS = [
  {
    name: 'name',
    label: 'Name',
    type: 'dropdown',
    component: 'select',
    validate: required,
  },
  { name: 'role', label: 'Role' },
  {
    name: 'gender',
    label: 'Gender',
    type: 'dropdown',
    component: 'select',
    options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' },
      { value: 'Unspecified', label: 'Unspecified' },
    ],
  },
  {
    name: 'race',
    label: 'Race',
    type: 'dropdown',
    component: 'select',
    options: [
      {
        value: 'AI/AN',
        label: 'American Indian or Alaska Native',
      },
      {
        value: 'Asian',
        label: 'Asian',
      },
      {
        value: 'Black or African American',
        label: 'Black or African American',
      },
      {
        value: 'Hispanic or Latino',
        label: 'Hispanic or Latino',
      },
      {
        value: 'Native Hawaiian or Other Pacific Islander',
        label: 'Native Hawaiian or Other Pacific Islander',
      },
      {
        value: 'White',
        label: 'White',
      },
      { value: 'Other', label: 'Other' },
      { value: 'Unspecified', label: 'Unspecified' },
    ],
  },
  {
    name: 'phone',
    label: 'Phone',
    type: 'dropdown',
    readType: 'phone',
    component: 'select',
    validate: phoneNumberVal,
  },
  {
    name: 'email',
    label: 'Email',
    type: 'dropdown',
    component: 'select',
    validate: emailVal,
  },
  { name: 'employer', label: 'Employer' },
  { name: 'position', label: 'Position' },
  {
    name: 'start_date',
    label: 'Start Date',
    type: 'date',
  },
  { name: 'expires', label: 'Expires', type: 'date' },
  { name: 'notes', label: 'Notes', multiline: true },
];

export const BOARD_MEMBER_MODAL_FIELDS = MEMBER_MODAL_FIELDS.map((row) =>
  row.name === 'role' ? { ...row, label: 'Board Seat' } : row,
);

export const STAFF_MEMBER_MODAL_FIELDS = MEMBER_MODAL_FIELDS.filter(
  (row) => row.name !== 'employer' && row.name !== 'position',
);
